import { useEffect, useRef, useState } from 'react';
import classNames from './DeleteTeacherModal.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { teacherService } from '@/school-management/services/teacher';
import { DeletedTeacherGroupData } from '@/school-management/types/response/teacher';
import {
  TeacherDeleteGroupActionRequest,
  TeacherDeleteGroupAction,
} from '@/school-management/types/request/teacher';
import { TeacherReportItem } from '@/school-management/types/response';
import AppModal from '@/components/AppModal/AppModal';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import PayAttention from './PayAttention/PayAttention';
import ClassesWithoutStudents from './ClassesWithoutStudents/ClassesWithoutStudents';
import AreYouSure from './AreYouSure/AreYouSure';
import Done, { DoneDescriptionData } from './Done/Done';
import SelectAction from './SelectAction/SelectAction';
import { useTeachers } from '@/admin/hooks/useTeachers';

interface DeleteTeacherModalProps {
  show: boolean;
  schoolId?: string;
  teacherId?: string;
  teacherName?: string;
  onDelete: (teacherId: string) => void;
  onCancel: () => void;
}

const DeleteTeacherModal = ({
  show,
  schoolId,
  teacherId,
  teacherName = '',
  onDelete,
  onCancel,
}: DeleteTeacherModalProps) => {
  const { t } = useAdminTranslation();
  const { teachers, isLoading: teachersLoading } = useTeachers();
  const isActiveRef = useRef<boolean>(false);
  const [notifMessage, setNotifMessage] = useState<string>('');
  const [view, setView] = useState<
    'PayAttention' | 'SelectAction' | 'ClassesWithoutStudents' | 'AreYouSure' | 'Done'
  >('PayAttention');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupsWithStudents, setGroupsWithStudents] = useState<DeletedTeacherGroupData[]>([]);
  const [groupsWithoutStudents, setGroupsWithoutStudents] = useState<DeletedTeacherGroupData[]>([]);
  const [actions, setActions] = useState<TeacherDeleteGroupActionRequest[]>([]);
  const [doneDescriptionData, setDoneDescriptionData] = useState<DoneDescriptionData>({
    deletedClasses: 0,
    deletedStudents: 0,
    reassigned: {},
  });

  useEffect(() => {
    isActiveRef.current = true;

    return () => {
      isActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (show) {
      setView('PayAttention');
      setIsLoading(false);
      setGroupsWithStudents([]);
      setGroupsWithoutStudents([]);
      setActions([]);
      setDoneDescriptionData({
        deletedClasses: 0,
        deletedStudents: 0,
        reassigned: {},
      });
      setNotifMessage('');
    }
  }, [show]);

  const calcDoneDescriptionData = (deleteResults: any[]) => {
    if (!deleteResults?.length) {
      return;
    }

    const groupsMapper = groupsWithStudents.reduce(
      (acc, group) => {
        acc[group.groupId] = group;
        return acc;
      },
      {} as {
        [groupId: string]: DeletedTeacherGroupData;
      }
    );

    const teachersMapper = (teachers || []).reduce(
      (acc, teacher) => {
        acc[teacher.userId] = teacher;
        return acc;
      },
      {} as {
        [teacherId: string]: TeacherReportItem;
      }
    );

    const newDoneDescriptionData: DoneDescriptionData = {
      ...doneDescriptionData,
      reassigned: {
        ...doneDescriptionData.reassigned,
      },
    };

    deleteResults.forEach(result => {
      if (!result?.isSuccessful) {
        return;
      }

      const group = groupsMapper[result?.groupId || ''];

      if (!group) {
        return;
      }

      if (result?.action === TeacherDeleteGroupAction.DeleteGroup) {
        newDoneDescriptionData.deletedClasses += 1;
        newDoneDescriptionData.deletedStudents += group.studentCount;
        return;
      }

      if (
        result?.action === TeacherDeleteGroupAction.ReplaceTeacher &&
        result.replacementTeacherId
      ) {
        const teacherName = `${teachersMapper[result.replacementTeacherId]?.firstName || ''} ${teachersMapper[result.replacementTeacherId]?.lastName || ''}`;

        if (!newDoneDescriptionData.reassigned[result.replacementTeacherId]) {
          newDoneDescriptionData.reassigned[result.replacementTeacherId] = {
            numberOfClasses: 0,
            numberOfStudents: 0,
            teacherName: teacherName,
          };
        }

        newDoneDescriptionData.reassigned[result.replacementTeacherId].numberOfClasses += 1;
        newDoneDescriptionData.reassigned[result.replacementTeacherId].numberOfStudents +=
          group.studentCount;

        return;
      }
    });

    setDoneDescriptionData(newDoneDescriptionData);
  };

  const deleteTeacher = async () => {
    if (!schoolId || !teacherId) {
      return;
    }

    const onError = () => {
      if (!isActiveRef.current) return;
      setIsLoading(false);
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      checkTeacher();
    };

    try {
      setIsLoading(true);
      const result = (await teacherService.deleteTeacher(schoolId, teacherId))?.data?.data || {};
      if (!isActiveRef.current) return;

      if (result?.isDeleted || result?.isTeacherDeleted) {
        // Wait 2 seconds for DynamoDB stream to update postgres
        await new Promise(resolve => setTimeout(resolve, 2000));

        onDelete(teacherId);
        return;
      }

      onError();
    } catch (error) {
      onError();
    }
  };

  const deleteTeacherWithActions = async (actions: TeacherDeleteGroupActionRequest[]) => {
    if (!schoolId || !teacherId) {
      return;
    }

    const onError = () => {
      if (!isActiveRef.current) return;
      setIsLoading(false);
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      checkTeacher();
    };

    try {
      setIsLoading(true);
      const result =
        (await teacherService.deleteTeacherWithActions(schoolId, teacherId, actions))?.data?.data ||
        {};

      if (!isActiveRef.current) return;
      calcDoneDescriptionData(result?.groupActionResults || []);

      if (result?.isDeleted || result?.isTeacherDeleted) {
        // Wait 2 seconds for DynamoDB stream to update postgres
        await new Promise(resolve => setTimeout(resolve, 2000));

        if (!isActiveRef.current) return;

        setIsLoading(false);
        setView('Done');
        return;
      }

      setIsLoading(false);
      onError();
    } catch (error) {
      onError();
    }
  };

  const checkTeacher = async () => {
    if (!schoolId || !teacherId) {
      return;
    }

    try {
      setIsLoading(true);

      const canBeDeletedData = await teacherService.checkIfTeacherCanBeDeleted(schoolId, teacherId);

      if (!isActiveRef.current) return;

      const groupsWithStudents = canBeDeletedData?.data?.data?.groupsWithStudents || [];
      const groupsWithoutStudents = canBeDeletedData?.data?.data?.groupsWithoutStudents || [];
      setGroupsWithStudents(groupsWithStudents);
      setGroupsWithoutStudents(groupsWithoutStudents);

      setIsLoading(false);

      if (canBeDeletedData?.data?.data?.canBeDeleted && groupsWithoutStudents.length > 0) {
        setView('ClassesWithoutStudents');
        return;
      }

      if (canBeDeletedData?.data?.data?.canBeDeleted) {
        deleteTeacher();
        return;
      }

      setView('SelectAction');
    } catch (error) {
      if (!isActiveRef.current) return;
      setIsLoading(false);
      setNotifMessage(t('dynamicForm.notification.generalError') || '');
      setView('PayAttention');
    }
  };

  const content =
    view === 'ClassesWithoutStudents' ? (
      <ClassesWithoutStudents
        groups={groupsWithoutStudents}
        onCancel={onCancel}
        onConfirm={() => {
          deleteTeacher();
        }}
      />
    ) : view === 'AreYouSure' ? (
      <AreYouSure
        teacherName={teacherName}
        groups={groupsWithStudents}
        onCancel={onCancel}
        onConfirm={() => {
          deleteTeacherWithActions(actions);
        }}
      />
    ) : view === 'Done' ? (
      <Done
        teacherName={teacherName}
        descriptionData={doneDescriptionData}
        //FIXME: this is a temporal solution till the backend will fix the response
        emptyGroupsPotentiallyDeleted={groupsWithoutStudents}
        onConfirm={() => {
          onDelete(teacherId || '');
        }}
      />
    ) : view === 'SelectAction' ? (
      <SelectAction
        teacherId={teacherId || ''}
        schoolId={schoolId}
        groups={groupsWithStudents}
        schoolTeachers={teachers || []}
        onCancel={onCancel}
        onConfirm={(actions: TeacherDeleteGroupActionRequest[]) => {
          const hasDeleteClass = !!actions.find(
            action => action.action === TeacherDeleteGroupAction.DeleteGroup
          );
          setActions(actions);
          if (hasDeleteClass) {
            setView('AreYouSure');
          } else {
            deleteTeacherWithActions(actions);
          }
        }}
      />
    ) : (
      <PayAttention
        teacherName={teacherName}
        onCancel={onCancel}
        onConfirm={() => {
          checkTeacher();
        }}
      />
    );

  return (
    <>
      <AppModal
        show={show}
        className={classNames.modalOverlay}
        modalContentClassName={classNames.modal}
        backdropClassName={classNames.backdrop}
        type='fade'
      >
        <div className={classNames.content}>{content}</div>
        {(isLoading || teachersLoading) && (
          <div className={classNames.loading}>
            <AppLoaderCircle />
          </div>
        )}
        <ActionNotification
          open={!!notifMessage}
          message={notifMessage}
          type={'error'}
          position='top-center'
          onClose={() => setNotifMessage('')}
          disablePortal
        />
      </AppModal>
    </>
  );
};

export default DeleteTeacherModal;
